/**
 * @file
 * DossierDrawer component displaying a side navigation menu.
 * Uses Ant Design for layout and icons.
 */
import { useEffect, useState } from 'react';
import './DossierDrawer.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Menu, MenuProps, Grid } from 'antd';
import {
  ApiOutlined,
  AuditOutlined,
  BankOutlined,
  BarChartOutlined,
  CalculatorOutlined,
  CreditCardOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  SnippetsOutlined,
  TeamOutlined,
  ToolOutlined,
  BookOutlined,
  SolutionOutlined,
  FileDoneOutlined,
  FileSyncOutlined,
  ProfileOutlined,
  FileAddOutlined,
  OrderedListOutlined,
  FileProtectOutlined,
  EditOutlined,
  AreaChartOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { menuConfig } from './dossierMenuConfig';
const { useBreakpoint } = Grid;

/**
 * @description
 * The DossierDrawer component displays a collapsible side menu
 * and enables navigation to different sections of the application.
 * It automatically adapts its view mode (collapsed or not) based on screen size.
 *
 * @returns {JSX.Element} The side menu component with integrated navigation.
 */
const DossierDrawer: React.FC = () => {
  const screens = useBreakpoint();
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const handleOnPointSuspensClick = () => {
    console.log('Local storage cleared.');
    navigate(menuConfig['menu-item-points-en-suspens'].path || '');
  };

  const getSelectedKey = () => {
    // Find key in menuConfig using current location
    return Object.entries(menuConfig).find(([, { path }]) => path === location.pathname)?.[0] || '';
  };

  const getParentKeys = (key: string): string[] => {
    if (!key) return [];
    return menuConfig[key]?.menuParents || [];
  };

  const onOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  const onMenuClick: MenuProps['onClick'] = (e) => {
    setSelectedKeys([e.key]);
  };

  /**
   * @description
   * Function to toggle between collapsed and expanded menu states.
   */
  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  /**
   * @description
   * Effect that watches for changes in screen size
   * and adjusts the `collapsed` state for small screens (xs, sm, md).
   */
  useEffect(() => {
    if ((screens.xs || screens.sm || screens.md) && !(screens.lg || screens.xl || screens.xxl)) {
      setCollapsed(true);
    }
  }, [screens]);

  // Update selectedKeys and openKeys whenever the route changes
  useEffect(() => {
    const foundKey = getSelectedKey();
    setSelectedKeys(foundKey ? [foundKey] : []);
    setOpenKeys(getParentKeys(foundKey));
  }, [location]);

  const items: MenuProps['items'] = [
    {
      key: 'menu-item-parametres',
      icon: <SettingOutlined />,
      label: <span data-testid="menu-item-parametres">Paramètres</span>,
      children: [
        {
          key: 'menu-item-general',
          label: <span data-testid="menu-item-general">Général</span>,
          icon: <FolderOpenOutlined />,
          onClick: () => navigate(menuConfig['menu-item-general'].path || ''),
        },
        {
          key: 'menu-item-fiscal',
          label: <span data-testid="menu-item-fiscal">Fiscal</span>,
          icon: <BankOutlined />,
          onClick: () => navigate(menuConfig['menu-item-fiscal'].path || ''),
        },
        {
          key: 'menu-item-comptabilite',
          label: <span data-testid="menu-item-comptabilite">Comptabilité</span>,
          icon: <CalculatorOutlined />,
          children: [
            {
              key: 'menu-item-exercices',
              label: <span data-testid="menu-item-exercices">Exercices</span>,
              icon: <CalendarOutlined />,
              onClick: () => navigate(menuConfig['menu-item-exercices'].path || ''),
            },
            {
              key: 'menu-item-journaux',
              label: <span data-testid="menu-item-journaux">Journaux</span>,
              icon: <BookOutlined />,
              onClick: () => navigate(menuConfig['menu-item-journaux'].path || ''),
            },
            {
              key: 'menu-item-plan-comptable',
              label: <span data-testid="menu-item-plan-comptable">Plan comptable</span>,
              icon: <ProfileOutlined />,
              onClick: () => navigate(menuConfig['menu-item-plan-comptable'].path || ''),
            },
            {
              key: 'menu-item-lettrage-auto',
              label: <span data-testid="menu-item-lettrage-auto">Lettrage automatique</span>,
              icon: <FileSyncOutlined />,
              onClick: () => navigate(menuConfig['menu-item-lettrage-auto'].path || ''),
            },
            {
              key: 'menu-item-politique-immobilisation',
              label: (
                <span data-testid="menu-item-politique-immobilisation">
                  Politique d'immobilisation
                </span>
              ),
              icon: <ToolOutlined />,
              onClick: () => navigate(menuConfig['menu-item-politique-immobilisation'].path || ''),
            },
          ],
        },
        {
          key: 'menu-item-analytique',
          label: <span data-testid="menu-item-analytique">Analytique</span>,
          icon: <AreaChartOutlined />,
          onClick: () => navigate(menuConfig['menu-item-analytique'].path || ''),
        },
        {
          key: 'menu-item-social',
          label: <span data-testid="menu-item-social">Social</span>,
          icon: <TeamOutlined />,
        },
        {
          key: 'menu-item-juridique',
          label: <span data-testid="menu-item-juridique">Juridique</span>,
          icon: <AuditOutlined />,
        },
        {
          key: 'menu-item-reglage-application',
          label: <span data-testid="menu-item-reglage-application">Réglage application</span>,
          icon: <ToolOutlined />,
        },
      ],
    },
    {
      key: 'menu-item-pre-comptabilite',
      icon: <FileTextOutlined />,
      label: <span data-testid="menu-item-pre-comptabilite">Pré-comptabilité</span>,
      children: [
        {
          key: 'menu-item-precompta-banque',
          label: <span data-testid="menu-item-precompta-banques">Banques</span>,
          icon: <BankOutlined />,
          onClick: () => navigate(menuConfig['menu-item-precompta-banques'].path || ''),
        },
        {
          key: 'menu-item-precompta-transactions',
          label: <span data-testid="menu-item-precompta-transactions">Transactions</span>,
          icon: <FolderOpenOutlined />,
          onClick: () => navigate(menuConfig['menu-item-precompta-transactions'].path || ''),
        },
      ],
    },
    {
      key: 'menu-item-points-en-suspens',
      icon: <ExclamationCircleOutlined />,
      label: 'Points en suspens',
      onClick: () => handleOnPointSuspensClick(),
    },
    {
      key: 'menu-item-production-comptable',
      icon: <BarChartOutlined />,
      label: <span data-testid="menu-item-production-comptable">Production comptable</span>,
      children: [
        {
          key: 'menu-item-pieces',
          label: <span data-testid="menu-item-pieces">Pièces</span>,
          icon: <FileAddOutlined />,
          onClick: () => navigate(menuConfig['menu-item-pieces'].path || ''),
        },
        {
          key: 'menu-item-etats-comptables',
          label: <span data-testid="menu-item-etats-comptables">États comptables</span>,
          icon: <ProfileOutlined />,
          children: [
            {
              key: 'menu-item-journal',
              label: <span data-testid="menu-item-journal">Journal</span>,
              icon: <BookOutlined />,
              onClick: () => navigate(menuConfig['menu-item-journal'].path || ''),
            },
            {
              key: 'menu-item-grand-livre',
              label: <span data-testid="menu-item-grand-livre">Grand livre</span>,
              icon: <SolutionOutlined />,
              onClick: () => navigate(menuConfig['menu-item-grand-livre'].path || ''),
            },
            {
              key: 'menu-item-balance',
              label: <span data-testid="menu-item-balance">Balance</span>,
              icon: <FileDoneOutlined />,
              onClick: () => navigate(menuConfig['menu-item-balance'].path || ''),
            },
          ],
        },
        {
          key: 'menu-item-modules',
          label: <span data-testid="menu-item-modules">Modules</span>,
          icon: <OrderedListOutlined />,
          children: [
            {
              key: 'menu-item-immobilisations',
              label: <span data-testid="menu-item-immobilisations">Immobilisations</span>,
              icon: <FileSyncOutlined />,
              onClick: () => navigate('/cabinet/immobilisations'),
            },
            {
              key: 'menu-item-comptes-courants',
              label: <span data-testid="menu-item-comptes-courants">Comptes courants</span>,
              icon: <FileProtectOutlined />,
              onClick: () => navigate('/cabinet/comptes-courants'),
            },
            {
              key: 'menu-item-emprunt',
              label: <span data-testid="menu-item-emprunt">Emprunt</span>,
              icon: <CreditCardOutlined />,
              onClick: () => navigate('/cabinet/emprunts'),
            },
            {
              key: 'menu-item-credits-bails',
              label: <span data-testid="menu-item-credits-bails">Crédits-bails</span>,
              icon: <EditOutlined />,
            },
            {
              key: 'menu-item-cut-off',
              label: <span data-testid="menu-item-cutt-off">Cut-off</span>,
              icon: <ApiOutlined />,
              children: [
                {
                  key: 'menu-item-cut-off-fnp',
                  label: <span data-testid="menu-item-cutt-off">Factures non parvenues</span>,
                  onClick: () => navigate(menuConfig['menu-item-cut-off-fnp'].path || ''),
                },
                {
                  key: 'menu-item-cut-off-aar',
                  label: <span data-testid="menu-item-cutt-off">Avoirs à recevoir</span>,
                  onClick: () => navigate(menuConfig['menu-item-cut-off-aar'].path || ''),
                },
                {
                  key: 'menu-item-cut-off-cca',
                  label: <span data-testid="menu-item-cutt-off">Charges constatées d’avance</span>,
                  onClick: () => navigate(menuConfig['menu-item-cut-off-cca'].path || ''),
                },
                {
                  key: 'menu-item-cut-off-pca',
                  label: <span data-testid="menu-item-cutt-off">Produits constatés d’avance</span>,
                  onClick: () => navigate(menuConfig['menu-item-cut-off-pca'].path || ''),
                },
                {
                  key: 'menu-item-cut-off-fae',
                  label: <span data-testid="menu-item-cutt-off">Factures à établir</span>,
                  onClick: () => navigate(menuConfig['menu-item-cut-off-fae'].path || ''),
                },
                {
                  key: 'menu-item-cut-off-aae',
                  label: <span data-testid="menu-item-cutt-off">Avoirs à établir</span>,
                  onClick: () => navigate(menuConfig['menu-item-cut-off-aae'].path || ''),
                },
                {
                  key: 'menu-item-cut-off-par',
                  label: <span data-testid="menu-item-cutt-off">Produits à recevoir</span>,
                  onClick: () => navigate(menuConfig['menu-item-cut-off-par'].path || ''),
                },
                {
                  key: 'menu-item-cut-off-cap',
                  label: <span data-testid="menu-item-cutt-off">Charges à payer</span>,
                  onClick: () => navigate(menuConfig['menu-item-cut-off-cap'].path || ''),
                },
              ],
            },
          ],
        },
        {
          key: 'menu-item-revision-comptes',
          label: <span data-testid="menu-item-revision-comptes">Révision des comptes</span>,
          icon: <FileProtectOutlined />,
        },
        {
          key: 'menu-item-etats-financiers',
          label: <span data-testid="menu-item-etats-financiers">États financiers</span>,
          icon: <SnippetsOutlined />,
        },
      ],
    },
    {
      key: 'menu-item-plaquette',
      icon: <SnippetsOutlined />,
      label: <span data-testid="menu-item-plaquette">Plaquette</span>,
    },
    {
      key: 'menu-item-teleportail',
      icon: <ApiOutlined />,
      label: <span data-testid="menu-item-teleportail">Téléportail</span>,
    },
    {
      key: 'menu-item-facturation',
      icon: <CreditCardOutlined />,
      label: <span data-testid="menu-item-facturation">Facturation</span>,
    },
  ];

  return (
    <div className="main-menu-container">
      <Button
        data-testid="menu-toggle-button"
        aria-expanded={!collapsed}
        type="primary"
        onClick={toggleCollapsed}
        className={`menu-toggle-button ${collapsed ? '' : 'expanded'}`}
        icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
      >
        {!collapsed} {/* Show text only when expanded */}
      </Button>
      <Menu
        selectedKeys={selectedKeys}
        openKeys={openKeys}
        onOpenChange={onOpenChange}
        onClick={onMenuClick}
        mode="inline"
        inlineCollapsed={collapsed}
        items={items}
        className="menu-content"
      />
    </div>
  );
};

export default DossierDrawer;
